// pb-wvdk-cart-upsell.js — Weekend van de Klant (3–4 Oct 2026) cart drawer upsell.
//
// While the campaign runs and the cart is below the towel threshold, this block
// replaces the regular upsell (#CartUpsellCard): a progress bar towards the free
// guest towel plus a swipe row of products that each close the gap on their own
// (related to the cart first, drawer bestsellers after). From the
// threshold on the block hides and the regular upsell returns. An empty cart
// shows just the "Bestel voor €25…" line instead of the bestsellers. The towel itself
// is added and discounted elsewhere, this file is display only.
//
// Markup + campaign window: snippets/pb-wvdk-cart-upsell.liquid.
// Docs: .cursor/references/weekend-van-de-klant.md
(function () {
  if (window.__pbWvdkCartUpsell) return;
  window.__pbWvdkCartUpsell = true;

  const drawer =
    document.querySelector('[data-pb-cart-drawer="primary"]') || document.getElementById('UnifiedCartDrawer');
  if (!drawer) return;

  const root = drawer.querySelector('[data-pb-wvdk]');
  if (!root) return;

  const remainingEl = root.querySelector('[data-pb-wvdk-remaining]');
  const fillEl = root.querySelector('[data-pb-wvdk-fill]');
  const productsEl = root.querySelector('[data-pb-wvdk-products]');
  const scrollerEl = root.querySelector('[data-pb-wvdk-scroller]');
  const listEl = root.querySelector('[data-pb-wvdk-list]');
  if (!remainingEl || !fillEl || !productsEl || !scrollerEl || !listEl) return;

  const START_MS = Number(root.dataset.start) * 1000;
  const END_MS = Number(root.dataset.end) * 1000;
  const THRESHOLD_CENTS = Number(root.dataset.threshold) || 2500;
  const ACTIVE_CLASS = 'pb-wvdk-active';

  // Same recommendation settings and fallback sources as the regular upsell:
  // the drawer's Liquid-embedded bestsellers (WONDR + POWR), then the upsell
  // collection. The embedded lists are used first because products.json can
  // come back empty for a collection that does render in Liquid.
  const intent = drawer.dataset.upsellIntent || 'related';
  const limit = Number(drawer.dataset.upsellLimit) || 8;
  const FALLBACK_EMBEDDED_IDS = ['CartBestsellerData1', 'CartBestsellerData2'];
  const FALLBACK_COLLECTION = drawer.dataset.upsellCollection || '';

  // Same exclusions as cart-progress-bar.js / cart-gift-handler.js: gift cards
  // and memberships never count towards (or get suggested for) a threshold.
  const EXCLUDED_PRODUCT_IDS = [
    'gid://shopify/Product/4849416896586', // Gift card
    'gid://shopify/Product/15652866294134', // Membership
    'gid://shopify/Product/15630492238198', // Membership
  ];

  // Attribution: counted as a regular cart upsell ATC in Converge ("Cart Upsell
  // ATC", see theme.liquid) and on the line, with its own context.
  const ATC_SOURCE = 'cart_upsell';
  const ATC_CONTEXT = 'wvdk';

  const log = (...args) => window.PBDebug?.log('[WVDK]', ...args);

  // --- Campaign window + preview ---

  // On any non-live theme (preview, theme editor) the block ignores the window,
  // so it can be tested before the campaign. ?wvdk_preview=0 switches that off
  // for the tab (to test the real window), ?wvdk_preview=1 switches it back on.
  // The live theme always follows the window, whatever the URL says.
  const PREVIEW_OFF_KEY = 'pb_wvdk_preview_off';
  function readPreviewFlag() {
    const role = window.Shopify?.theme?.role;
    const isLive = role ? role === 'main' : true;
    if (isLive) return false;
    let param = null;
    try {
      param = new URLSearchParams(window.location.search).get('wvdk_preview');
    } catch {
      /* ignore */
    }
    try {
      if (param === '0') sessionStorage.setItem(PREVIEW_OFF_KEY, '1');
      if (param === '1') sessionStorage.removeItem(PREVIEW_OFF_KEY);
      return sessionStorage.getItem(PREVIEW_OFF_KEY) !== '1';
    } catch {
      return param !== '0';
    }
  }
  const preview = readPreviewFlag();

  function inWindow() {
    if (preview) return true;
    const now = Date.now();
    return now >= START_MS && now < END_MS;
  }

  // Re-check at the start and end of the window for pages left open across it.
  function scheduleBoundaryCheck() {
    const now = Date.now();
    const next = now < START_MS ? START_MS : now < END_MS ? END_MS : null;
    if (next === null) return;
    const delay = next - now + 1000;
    if (delay > 2147483647) return; // beyond setTimeout's range
    setTimeout(() => {
      scheduleRender();
      scheduleBoundaryCheck();
      document.dispatchEvent(new CustomEvent('pb:wvdk:change'));
    }, delay);
  }

  // --- Helpers ---

  function fmtEUR(cents) {
    const v = Math.max(0, Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(v);
    } catch {
      return '€' + v.toFixed(2).replace('.', ',');
    }
  }

  // products.json returns "8.95" (euros), recommendations return 895 (cents).
  function priceToCents(raw) {
    if (typeof raw === 'string') {
      const n = parseFloat(raw.replace(',', '.'));
      return Number.isFinite(n) ? Math.round(n * 100) : 0;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }

  function numericId(gid) {
    if (!gid) return '';
    const s = String(gid);
    if (/^\d+$/.test(s)) return s;
    const match = s.match(/\/(\d+)\s*$/);
    return match ? match[1] : '';
  }

  function escapeHTML(value) {
    return String(value ?? '').replace(
      /[&<>"']/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c],
    );
  }

  function imageSrc(product, variant, width) {
    const raw =
      variant?.featured_image?.src ||
      product?.images?.[0]?.src ||
      product?.featured_image?.src ||
      product?.featured_image ||
      (typeof product?.images?.[0] === 'string' ? product.images[0] : '') ||
      '';
    if (!raw) return '';
    const src = raw.startsWith('//') ? `https:${raw}` : raw;
    return `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
  }

  // Short card names as in the design ("Viva Vanilla", "Ocean Breeze XL"). Bars
  // share their scent (custom.collection_page_rating_for) across formats, so the
  // format from the title ("SCENT | XL Shampoo Bar") is appended without the
  // generic "Shampoo"/"Bar" words: "Vegan Honey" vs "Vegan Honey Conditioner".
  // Other products use custom.product_card_title, like cart-upsell.js.
  function cardTitle(product) {
    const title = String(product?.title || '').trim();
    const custom = String(product?.card_title || '').trim();
    const ratingFor = String(product?.rating_for || '').trim();
    const naming = String(product?.cart_naming || '')
      .trim()
      .toLowerCase();

    if (naming !== 'bar') return custom || title;
    if (!ratingFor) return title;

    const scent = ratingFor.toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase());
    const format = (title.split('|')[1] || '')
      .replace(/\b(shampoo|bar)\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
    return format ? `${scent} ${format}` : scent;
  }

  // --- Cart ---

  async function waitForPBCart(maxMs = 5000) {
    const start = Date.now();
    while (!(window.PBCart && typeof window.PBCart.current === 'function')) {
      if (Date.now() - start > maxMs) return false;
      await new Promise((r) => setTimeout(r, 50));
    }
    return true;
  }

  async function currentCart(detailCart) {
    if (detailCart && detailCart.lines) return detailCart;
    if (!(await waitForPBCart())) return null;
    try {
      return await window.PBCart.current();
    } catch {
      return null;
    }
  }

  function cartLines(cart) {
    return (cart?.lines?.edges || []).map((edge) => edge?.node).filter(Boolean);
  }

  // Gift lines as the Auto Gifter app counts them (isGiftLine in its engine,
  // github.com/BRAUZZ-BV/auto-gifter-test lib/engine.js): the towel itself
  // (_auto_gift) and theme gifts (_pb_gift, _beauty_bag).
  function isGiftLine(line) {
    const attrs = {};
    for (const a of line.attributes || []) attrs[a.key] = a.value;
    return attrs._auto_gift === 'true' || Boolean(attrs._pb_gift) || attrs._beauty_bag === 'true';
  }

  // Cents, after line discounts (line cost.totalAmount, as the app reads it),
  // without gift lines, gift cards and memberships.
  function qualifyingSubtotal(lines) {
    const euros = lines.reduce((sum, line) => {
      const productId = line.merchandise?.product?.id || '';
      if (EXCLUDED_PRODUCT_IDS.includes(productId)) return sum;
      if (isGiftLine(line)) return sum;
      return sum + parseFloat(line.cost?.totalAmount?.amount || 0);
    }, 0);
    return Math.round(euros * 100);
  }

  // --- Product sources ---

  const recCache = new Map();
  function fetchRecommendations(productId) {
    if (!recCache.has(productId)) {
      const url = `/recommendations/products.json?product_id=${productId}&limit=10&intent=${intent}`;
      recCache.set(
        productId,
        fetch(url)
          .then((r) => r.json())
          .then((d) => d.products || [])
          .catch(() => []),
      );
    }
    return recCache.get(productId);
  }

  const collectionCache = new Map();
  function fetchCollection(handle) {
    if (!collectionCache.has(handle)) {
      collectionCache.set(
        handle,
        fetch(`/collections/${handle}/products.json?limit=50`)
          .then((r) => r.json())
          .then((d) => d.products || [])
          .catch(() => []),
      );
    }
    return collectionCache.get(handle);
  }

  // Bestseller JSON rendered by unified-cart-drawer.liquid (prices in cents).
  function readEmbeddedProducts(scriptId) {
    try {
      const products = JSON.parse(document.getElementById(scriptId)?.textContent || '[]');
      return Array.isArray(products) ? products : [];
    } catch {
      return [];
    }
  }

  // Round-robin over the per-cart-product lists so every cart item gets its
  // best match near the front, instead of the first item taking every slot.
  function interleave(lists) {
    const out = [];
    const max = Math.max(0, ...lists.map((l) => l.length));
    for (let i = 0; i < max; i++) {
      for (const list of lists) if (list[i]) out.push(list[i]);
    }
    return out;
  }

  function availableVariant(product) {
    if (!product || product.available === false) return null;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    return variants.find((v) => v && v.available !== false && !v.requires_selling_plan) || null;
  }

  // Collection/recommendation JSON has no custom metafields: fetch card naming
  // and the review score via the Storefront API. A product missing from the
  // response is not on the cart's sales channel, so PBCart.addLine would throw.
  const metaCache = new Map(); // product gid -> meta | null
  async function enrich(picks) {
    const cfg = window.PB_UNIFIED_CART || {};
    const missing = picks.map((p) => p.gid).filter((gid) => !metaCache.has(gid));

    if (missing.length && cfg.endpoint && cfg.token) {
      const query =
        'query($ids:[ID!]!){nodes(ids:$ids){... on Product{id title availableForSale ' +
        'cardTitle: metafield(namespace:"custom",key:"product_card_title"){value} ' +
        'ratingFor: metafield(namespace:"custom",key:"collection_page_rating_for"){value} ' +
        'cartNaming: metafield(namespace:"custom",key:"cart_naming"){value} ' +
        'rating: metafield(namespace:"reviews",key:"rating"){value}}}}';

      try {
        const res = await fetch(cfg.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': cfg.token,
          },
          body: JSON.stringify({ query, variables: { ids: missing } }),
        });
        const json = await res.json();
        const nodes = json?.data?.nodes;
        if (Array.isArray(nodes)) {
          missing.forEach((gid, i) => {
            const node = nodes[i];
            if (!node || node.availableForSale === false) {
              metaCache.set(gid, null);
              return;
            }
            let rating = '';
            try {
              const value = Number(JSON.parse(node.rating?.value || '{}').value);
              if (value > 0) rating = value.toFixed(2);
            } catch {
              /* no rating */
            }
            metaCache.set(gid, {
              card_title: node.cardTitle?.value || '',
              rating_for: node.ratingFor?.value || '',
              cart_naming: node.cartNaming?.value || '',
              rating,
            });
          });
        }
      } catch (e) {
        log('metafield lookup failed', e);
      }
    }

    return picks
      .filter((p) => metaCache.get(p.gid) !== null)
      .map((p) => {
        const meta = metaCache.get(p.gid) || {};
        return { ...p, title: cardTitle({ ...p.product, ...meta }), rating: meta.rating || '' };
      });
  }

  // Products that reach the threshold on their own: related to the cart first,
  // then the fallback sources. Never something already in the cart.
  async function loadProducts(lines, remainingCents) {
    const inCart = new Set(lines.map((l) => numericId(l.merchandise?.product?.id)).filter(Boolean));
    const seen = new Set();
    const picks = [];
    const wanted = limit * 2; // headroom for products dropped by enrich()

    function collect(products) {
      for (const product of products) {
        if (picks.length >= wanted) return;
        const id = numericId(product?.id);
        if (!id || seen.has(id) || inCart.has(id)) continue;
        seen.add(id);
        const gid = `gid://shopify/Product/${id}`;
        if (EXCLUDED_PRODUCT_IDS.includes(gid)) continue;
        const variant = availableVariant(product);
        if (!variant) continue;
        const price = priceToCents(variant.price);
        if (price < remainingCents) continue;
        picks.push({ gid, product, variant, price });
      }
    }

    collect(interleave(await Promise.all([...inCart].map(fetchRecommendations))));
    if (picks.length < limit) {
      collect(interleave(FALLBACK_EMBEDDED_IDS.map(readEmbeddedProducts)));
    }
    if (picks.length < limit && FALLBACK_COLLECTION) {
      collect(await fetchCollection(FALLBACK_COLLECTION));
    }

    return (await enrich(picks)).slice(0, limit);
  }

  // --- Rendering ---

  function setActive(active) {
    root.hidden = !active;
    drawer.classList.toggle(ACTIVE_CLASS, active);
    setEmptyMode(false);
  }

  // Empty cart: only the "Bestel voor €25…" line, in place of the bestsellers
  // carousel, which pb-wvdk-cart-upsell.css hides while EMPTY_CLASS is on.
  const EMPTY_CLASS = 'pb-wvdk-empty';
  const bestsellersCard = document.getElementById('CartBestsellers');
  const upsellCard = document.getElementById('CartUpsellCard');
  function setEmptyMode(empty) {
    root.classList.toggle('is-empty-cart', empty);
    drawer.classList.toggle(EMPTY_CLASS, empty);
    const anchor = empty ? bestsellersCard : upsellCard;
    if (!anchor || anchor.parentNode !== root.parentNode) return;
    if (empty && root.nextElementSibling !== anchor) anchor.before(root);
    if (!empty && root.previousElementSibling !== anchor) anchor.after(root);
  }

  function showEmptyCart() {
    setActive(false);
    setEmptyMode(true);
    root.hidden = false;
  }

  function cardHTML(item) {
    const title = escapeHTML(item.title);
    const img = imageSrc(item.product, item.variant, 120);
    const rating = item.rating
      ? ` · ${item.rating} <span class="pb-wvdk-card__star" aria-hidden="true">★</span><span class="visually-hidden"> sterren</span>`
      : '';

    return `
      <li class="pb-wvdk-card">
        <div class="pb-wvdk-card__media">
          ${img ? `<img src="${escapeHTML(img)}" alt="" width="56" height="56" loading="lazy" draggable="false">` : ''}
        </div>
        <div class="pb-wvdk-card__info">
          <p class="pb-wvdk-card__title" title="${title}">${title}</p>
          <p class="pb-wvdk-card__meta">${fmtEUR(item.price)}${rating}</p>
        </div>
        <button
          type="button"
          class="pb-wvdk-card__add"
          data-variant-id="${escapeHTML(item.variant.id)}"
          data-cvg-atc
          data-atc-source="${ATC_SOURCE}"
          data-atc-context="${ATC_CONTEXT}"
          aria-label="${title} toevoegen aan je winkelwagen"
        >+</button>
      </li>
    `;
  }

  function updateFade() {
    const max = scrollerEl.scrollWidth - scrollerEl.clientWidth;
    root.classList.toggle('is-scroll-end', max <= 2 || scrollerEl.scrollLeft >= max - 2);
  }

  let lastListKey = '';
  function renderProducts(items) {
    const key = items.map((i) => i.variant.id).join(',');
    if (key === lastListKey) return;
    lastListKey = key;

    productsEl.hidden = !items.length;
    listEl.innerHTML = items.map(cardHTML).join('');
    scrollerEl.scrollLeft = 0;
    requestAnimationFrame(updateFade);
  }

  let renderToken = 0;
  async function render(detailCart) {
    const token = ++renderToken;

    if (!inWindow()) {
      setActive(false);
      return;
    }

    const cart = await currentCart(detailCart);
    if (token !== renderToken) return;

    const lines = cartLines(cart);
    if (!lines.length) {
      showEmptyCart();
      return;
    }

    const subtotal = qualifyingSubtotal(lines);
    const remaining = THRESHOLD_CENTS - subtotal;
    if (remaining <= 0) {
      log('threshold reached', subtotal);
      setActive(false);
      return;
    }

    // "€6,10" as in the design (Intl puts a space after the euro sign).
    remainingEl.textContent = fmtEUR(remaining).replace(/^€\s+/, '€');
    fillEl.style.width = `${Math.min(100, Math.max(0, (subtotal / THRESHOLD_CENTS) * 100))}%`;
    setActive(true);

    const items = await loadProducts(lines, remaining);
    if (token !== renderToken) return;
    log('render', { subtotal, remaining, items: items.length });
    renderProducts(items);
  }

  let renderTimer = null;
  let pendingCart = null;
  function scheduleRender(cart) {
    if (cart) pendingCart = cart;
    clearTimeout(renderTimer);
    renderTimer = setTimeout(() => {
      const c = pendingCart;
      pendingCart = null;
      render(c);
    }, 100);
  }

  // --- Interaction ---

  listEl.addEventListener('click', async (e) => {
    const btn = e.target.closest('.pb-wvdk-card__add');
    if (!btn || btn.disabled || !window.PBCart) return;

    btn.disabled = true;
    btn.classList.add('is-loading');
    try {
      // addLine dispatches pb:cart:updated (and cart:open) itself.
      await window.PBCart.addLine(btn.dataset.variantId, 1, [
        { key: '_added_via', value: ATC_SOURCE },
        { key: '_added_via_context', value: ATC_CONTEXT },
      ]);
    } catch (err) {
      console.warn('[PB WVDK] add to cart failed', err);
      btn.textContent = '!';
      setTimeout(() => {
        btn.textContent = '+';
      }, 1200);
    } finally {
      btn.disabled = false;
      btn.classList.remove('is-loading');
    }
  });

  // Mouse drag to scroll the row on desktop (touch and trackpads scroll natively).
  let drag = null;
  let suppressClick = false;
  scrollerEl.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    drag = { x: e.clientX, left: scrollerEl.scrollLeft, moved: false };
  });
  window.addEventListener('pointermove', (e) => {
    if (!drag) return;
    const dx = e.clientX - drag.x;
    if (!drag.moved && Math.abs(dx) < 5) return;
    drag.moved = true;
    scrollerEl.classList.add('is-dragging');
    scrollerEl.scrollLeft = drag.left - dx;
  });
  window.addEventListener('pointerup', () => {
    if (!drag) return;
    suppressClick = drag.moved;
    drag = null;
    scrollerEl.classList.remove('is-dragging');
    setTimeout(() => {
      suppressClick = false;
    }, 0);
  });
  scrollerEl.addEventListener(
    'click',
    (e) => {
      if (!suppressClick) return;
      e.preventDefault();
      e.stopPropagation();
    },
    true,
  );

  scrollerEl.addEventListener('scroll', updateFade, { passive: true });
  window.addEventListener('resize', updateFade);

  document.addEventListener('pb:cart:updated', (e) => scheduleRender(e.detail?.cart));
  document.addEventListener('cart:open', () => {
    scheduleRender();
    requestAnimationFrame(updateFade);
  });

  // Shared with the Pick & Mix notice (assets/pb-wvdk-pick-mix.js), so the
  // window, preview rule, threshold and subtotal basis live in one place.
  window.PBWvdk = {
    inWindow,
    thresholdCents: THRESHOLD_CENTS,
    qualifyingSubtotal,
    formatAmount: (cents) => fmtEUR(cents).replace(/^€\s+/, '€'),
  };

  if (preview) log('preview mode on');
  scheduleBoundaryCheck();
  scheduleRender();
})();
